import { Clock, Laptop, ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const courses = [
  {
    title: 'Formación Guardia de Seguridad Privada',
    description: 'Formación integral en prevención de riesgos y salud ocupacional. Certificación oficial reconocida por organismos competentes.',
    duration: '90 horas',
    modality: 'Presencial + Online',
    image: '/institu1-formacion.webp',
    highlight: true,
    accent: 'from-blue-500 to-cyan-500',
    link: '/curso/guardia-seguridad-privada',
  },
  {
    title: 'Especialización Guardia de Seguridad',
    description: 'Profundiza tus competencias en seguridad privada con técnicas avanzadas y especialización profesional.',
    duration: '36 horas',
    modality: 'Presencial + Online',
    image: '/instituteroom.webp',
    highlight: false,
    accent: 'from-violet-500 to-purple-500',
    link: '/curso/especializacion-guardia',
  },
  {
    title: 'Perfeccionamiento Guardia de Seguridad',
    description: 'Actualiza y perfecciona tus habilidades como guardia de seguridad con formación complementaria certificada.',
    duration: '36 horas',
    modality: 'Presencial + Online',
    image: '/institu2-perfec.webp',
    highlight: false,
    accent: 'from-emerald-500 to-teal-500',
    link: '/curso/perfeccionamiento-guardia',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function FeaturedCourses() {
  return (
    <section id="cursos" className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-500/5 to-cyan-500/5 rounded-full blur-3xl" />

        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-blue-500/20 text-blue-400 text-sm font-medium rounded-full mb-4">
            Formación Profesional
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 font-heading">
            Cursos{' '}
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Destacados
            </span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Descubre nuestros programas de capacitación profesional con certificación reconocida
          </p>
        </motion.div>

        {/* Courses grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {courses.map((course, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -10, scale: 1.02 }}
              className="group bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-300 flex flex-col h-full relative"
            >
              {/* Popular badge */}
              {course.highlight && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute top-4 right-4 z-20"
                >
                  <div className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg shadow-orange-500/30">
                    <Sparkles className="w-3 h-3" />
                    Popular
                  </div>
                </motion.div>
              )}

              {/* Unavailable badge */}
              {course.unavailable && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute top-4 right-4 z-20"
                >
                  <div className="flex items-center gap-1.5 bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg shadow-slate-500/30">
                    No disponible por el momento
                  </div>
                </motion.div>
              )}

              {/* Image container */}
              <div className="relative h-52 overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-t ${course.accent} opacity-0 group-hover:opacity-30 transition-opacity duration-500 z-10`} />
                <img
                  src={course.image}
                  alt={course.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
                {/* Bottom gradient */}
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-slate-900 to-transparent z-10" />
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                  {course.title}
                </h3>
                <p className="text-slate-400 text-sm mb-6 line-clamp-3 flex-grow leading-relaxed">
                  {course.description}
                </p>

                {/* Meta info */}
                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-blue-400" />
                    </div>
                    <span>{course.duration}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                      <Laptop className="w-4 h-4 text-cyan-400" />
                    </div>
                    <span>{course.modality}</span>
                  </div>
                </div>

                {/* CTA Button */}
                <Link to={course.link}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full bg-gradient-to-r ${course.accent} text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg group/btn cursor-pointer`}
                  >
                    Más Información
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </motion.div>
                </Link>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* View all link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center mt-12"
        >
          <Link to="/cursos">
            <motion.button
              whileHover={{ scale: 1.05 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl border border-white/20 backdrop-blur-sm transition-all"
            >
              Ver Catálogo Completo
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
